// De kern van Bak en Bezorg: koppelt de wereld (stad, verkeer, scooter) aan de spelregels (shift, orders)
// en aan de interface. Simuleert met een vaste tijdstap van 1/60 s, zodat het spel op elk scherm
// (60, 90 of 120 Hz) even snel loopt. Tekenen gebeurt één keer per beeldverversing.
import { SoundEngine } from '../audio/SoundEngine.js';
import { CityMap } from '../world/CityMap.js';
import { Scooter } from '../vehicle/Scooter.js';
import { applyMixins } from './mixins.js';
import { InputMixin } from './input.js';
import { WeatherMixin } from './weather.js';
import { PoliceMixin } from './police.js';
import { RenderWorldMixin } from '../render/world.js';
import { RenderActorsMixin } from '../render/actors.js';
import { RenderTargetsMixin } from '../render/targets.js';
import { RenderCargoMixin } from '../render/cargo.js';
import { RenderMarketMixin } from '../render/market.js';
import { Shift, SHIFT_SECONDS, RACE_BONUS } from './shift';
import { capacityFor, DISTRICT_NAMES, euro, rankFor, STREET_COIN_EURO } from './economy';
import { extendForDetour, seededRng, timeLeft } from './orders';
import { planRoute, raceIsFair } from './route';
import { baseZoomFor, stepZoom, zoomTarget } from './camera';
import { EVENT_TEXT } from './events';
import { districtAt } from './districts';
import { newlyEarned } from './achievements';
import { todayKey, writeSave } from '../state/save';
import { clearSavedShift, readSavedShift, writeSavedShift } from '../state/shiftSave';
import { t } from '../i18n';
import { SOUND_SETTINGS, levelGain } from '../audio/mix';
import { trackPage } from '../utils/analytics';

const STEP = 1 / 60;
const MAX_STEPS_PER_FRAME = 5;
/** Onder deze snelheid (≈12 km/u) telt rustig uitrollen in een zone als stilstaan. */
const DWELL_SPEED = 1.4;
const LOAD_SECONDS = 0.6;
const DELIVER_SECONDS = 0.5;
const BAKERY = { x: 1270, y: 1055, r: 45 };
/** Waar Sjors zijn bestelling inlaadt (Knus Expres Bakkerij & Depot). */
const SJORS_DEPOT = { x: 650, y: 1250 };

export class BezorgerGame {
  /**
   * @param {{ canvas: HTMLCanvasElement, save: import('../state/save').SaveState, storage: Storage | null, ui: any }} opts
   */
  constructor({ canvas, save, storage, ui }) {
    this.canvas = canvas;
    this.ctx = canvas.getContext('2d');
    this.save = save;
    this.storage = storage;
    this.ui = ui;
    this.sound = new SoundEngine();
    if (!save.settings.sound) this.sound.enabled = false;
    for (const [key, channel] of Object.entries(SOUND_SETTINGS)) this.sound.setChannelLevel(channel, levelGain(save.settings[key]));
    this.map = new CityMap();
    this.scooter = new Scooter(1220, 1140);
    this.scooter.angle = Math.PI;
    this.applyLoadout();

    this.camera = { x: this.scooter.x, y: this.scooter.y, zoom: 1 };
    this.shake = 0;
    this.time = 0;
    this.mode = 'menu'; // 'menu' | 'shift' | 'freeride'
    this.paused = false;
    this.shift = null;
    this.targets = [];
    this.activeTarget = null;
    this.dwell = { id: null, t: 0 };
    this.streetMoney = 0;
    this.isDaily = false;
    this.finedThisShift = false;
    this.loadBatch = null;
    this.fullLoadDone = false;

    // Effecten die de tekenmodules gebruiken
    this.floatingTexts = [];
    this.shockParticles = [];
    this.confettiParticles = [];

    this.nightMode = save.settings.theme;
    this.isNight = this.calculateIsNight();
    this.weather = 'sunny';
    this.weatherMode = 'dynamic';
    this.weatherTimer = 0;
    this.thunderFlashTimer = 0;
    this.thunderCooldown = 15;
    this.initWeatherParticles();
    this.resetPolice();

    this.map.onPoliceIntercept = (car) => this.handlePoliceIntercept(car, 'kat');
    this.map.onRivalDelivery = () => {};
    this.map.onRivalRaceArrive = (destId) => this.onRivalArrived(destId);
    this.scooter.onStuntJump = () => {
      this.save.stats.stunts += 1;
      this.rewardMoney(3, t('float.stunt'));
      this.floatingTexts.push({ x: this.scooter.x, y: this.scooter.y - 60, vy: -0.8, text: t('float.overCanal'), color: '#38bdf8', alpha: 1 });
      this.checkAchievements();
    };
    this.scooter.onDriftBoost = () => {
      this.floatingTexts.push({ x: this.scooter.x, y: this.scooter.y - 36, vy: -1, text: t('float.boost'), color: '#38bdf8', alpha: 1 });
      this.sound.playFriendlyDoubleHorn?.();
    };
    this.scooter.steerAssist = save.settings.assist;
    this.hitStop = 0;
    // Lading op de bak wiebelt mee: een veer met hoek en snelheid
    this.cargoWobble = { angle: 0, vel: 0 };
    this.motionQuery = matchMedia('(prefers-reduced-motion: reduce)');
    this.motionQuery.addEventListener?.('change', () => this.applyMotion());
    this.applyMotion();

    this.controlMode = save.settings.controls;
    this.setupInputs();
    this.resizeCanvas();
    window.addEventListener('resize', () => this.resizeCanvas());
    window.visualViewport?.addEventListener('resize', () => this.resizeCanvas());
    // App verlaten (andere app, scherm uit, tabblad weg): pauzeren en de shift bewaren.
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) return;
      if (this.mode !== 'menu') this.pause();
      this.saveShiftProgress();
    });
    window.addEventListener('pagehide', () => {
      if (this.mode !== 'menu') this.pause();
      this.saveShiftProgress();
    });

    this.hasInteracted = false;
    const interacted = () => {
      this.hasInteracted = true;
      this.lastTime = performance.now();
    };
    window.addEventListener('pointerdown', interacted, { once: true });
    window.addEventListener('keydown', interacted, { once: true });

    this.accumulator = 0;
    this.lastTime = performance.now();
    requestAnimationFrame((t) => this.frame(t));
  }

  // ---------------------------------------------------------------------------
  // Spelstand
  // ---------------------------------------------------------------------------

  persist() {
    writeSave(this.storage, this.save);
  }

  /** Neemt bak, velgen, motor, lak en voertuig uit de spelstand over op de scooter. */
  applyLoadout() {
    const s = this.save;
    this.scooter.setVehicleType(s.vehicle);
    this.scooter.boxType = s.box;
    this.scooter.hasSportVelgen = s.wheels === 'sport';
    this.scooter.hasTurbo = s.engine === 'turbo';
    this.scooter.paintColor = s.paint;
  }

  setSave(next) {
    this.save = next;
    this.applyLoadout();
    this.persist();
  }

  get capacity() {
    return capacityFor(this.save.box, this.save.vehicle);
  }

  get carryContext() {
    return { box: this.save.box, vehicle: this.save.vehicle, weather: this.weather };
  }

  /** Kleine beloning in euro's (munten op straat, stunts, file-slalom). Telt mee voor de dag. */
  rewardMoney(amount, label) {
    this.earn(amount);
    this.streetMoney += amount;
    this.persist();
    this.floatingTexts.push({ x: this.scooter.x, y: this.scooter.y - 40, vy: -1, text: t('float.reward', { label, money: euro(amount) }), color: '#f59e0b', alpha: 1 });
    this.sound.playCoinSound();
  }

  /** Geld bijschrijven (of afschrijven bij een negatief bedrag): portemonnee, totaal en de lopende shift. */
  earn(amount) {
    this.save.wallet = Math.max(0, Math.round((this.save.wallet + amount) * 100) / 100);
    if (amount > 0) this.save.lifetimeEarnings = Math.round((this.save.lifetimeEarnings + amount) * 100) / 100;
    this.shift?.addEarnings(amount);
  }

  /** Controleert prestaties en betaalt de beloning uit. */
  checkAchievements(ended = false) {
    const sh = this.shift;
    const facts = sh
      ? {
          deliveries: sh.delivered.length,
          bestCombo: sh.bestCombo,
          dayStars: sh.summary().shiftStars,
          fined: this.finedThisShift,
          delivered: sh.delivered,
          fullLoadDelivered: this.fullLoadDone,
          ended,
          daily: this.isDaily,
        }
      : null;
    for (const a of newlyEarned(this.save, facts)) {
      this.save.achievements = [...this.save.achievements, a.id];
      this.earn(a.reward);
      this.sound.playUpgradeSound();
      this.ui.toast(t('toast.achievement', { name: a.name, money: euro(a.reward) }), 'good');
    }
    this.persist();
  }

  // ---------------------------------------------------------------------------
  // Modi
  // ---------------------------------------------------------------------------

  /** @param {{ daily?: boolean, resume?: boolean }} [opts] daily: de uitdaging van vandaag, met voor iedereen dezelfde bestellingen; resume: de shift wordt daarna hersteld uit de opslag */
  startShift({ daily = false, resume = false } = {}) {
    trackPage(daily ? '/dagelijkse-shift' : '/shift', daily ? 'Dagelijkse shift' : 'Shift');
    if (!resume) clearSavedShift(this.storage);
    this.sound.init();
    this.sound.setWeather(this.weather);
    const rank = rankFor(this.save.stars);
    this.isDaily = daily;
    // Geleide eerste minuut: alleen voor wie nog nooit een shift heeft gespeeld (niet bij de uitdaging of hervatten)
    this.guided = !daily && !resume && !this.save.tutorialDone;
    this.shift = new Shift({
      rng: daily && !resume ? seededRng(Number(todayKey().replaceAll('-', ''))) : Math.random,
      rank: rank.rank,
      districts: rank.districts,
      destinations: this.map.destinations,
      bakery: BAKERY,
      capacity: this.capacity,
      raceFair: (dest) => raceIsFair(BAKERY, SJORS_DEPOT, dest, this.shift?.routeOptions),
      bridgeOpen: () => this.bridgeOpen(),
      calm: this.guided,
    });
    // Geen boetes of achtervolgingen tijdens de uitleg
    this.resetPolice();
    if (this.guided) this.policeImmunityTimer = 1e6;
    // In een shift bepalen de gebeurtenissen het weer (voorspelbaar en aangekondigd), niet het toeval.
    this.weatherMode = 'scripted';
    this.setWeather('sunny', false);
    this.map.setClosures([]);
    this.bridgeWasOpen = false;
    this.districtNow = null;
    this.districtCandidate = null;
    this.rankAtStart = rank.rank;
    this.starsAtStart = this.save.stars;
    this.streetMoney = 0;
    this.shiftDistance = 0;
    this.finedThisShift = false;
    this.loadBatch = null;
    this.fullLoadDone = false;
    this.map.stopRivalRace?.(true);
    this.respawnAtBakery();
    this.mode = 'shift';
    this.paused = false;
    this.resizeCanvas();
    this.ui.onModeChange('shift');
  }

  /** Is er een shift bewaard die je kunt hervatten? */
  get savedShift() {
    const saved = readSavedShift(this.storage);
    return saved && saved.rank === rankFor(this.save.stars).rank ? saved : null;
  }

  /** Bewaart de lopende shift. Doet niets buiten een shift. */
  saveShiftProgress() {
    if (this.mode !== 'shift' || !this.shift || this.shift.ended || this.shift.isOver) return;
    const s = this.scooter;
    writeSavedShift(this.storage, {
      version: 1,
      savedAt: Date.now(),
      daily: this.isDaily,
      rank: this.rankAtStart,
      snapshot: this.shift.snapshot(),
      scooter: { x: s.x, y: s.y, angle: s.angle },
      streetMoney: this.streetMoney ?? 0,
      shiftDistance: this.shiftDistance ?? 0,
      finedThisShift: !!this.finedThisShift,
      starsAtStart: this.starsAtStart ?? this.save.stars,
    });
  }

  /** Gaat verder waar je gebleven was. De bestellingen en de klok staan zoals je ze achterliet. */
  resumeSavedShift() {
    const saved = this.savedShift;
    if (!saved) return false;
    this.startShift({ daily: saved.daily, resume: true });
    this.shift.restore(saved.snapshot);
    this.map.setClosures(this.shift.closures);
    this.setWeather(this.shift.weather, false);
    this.starsAtStart = saved.starsAtStart;
    this.streetMoney = saved.streetMoney;
    this.shiftDistance = saved.shiftDistance;
    this.finedThisShift = saved.finedThisShift;
    const s = this.scooter;
    s.x = saved.scooter.x;
    s.y = saved.scooter.y;
    s.angle = saved.scooter.angle;
    s.speed = 0;
    this.camera.x = s.x;
    this.camera.y = s.y;
    this.pause();
    return true;
  }

  /** De uitleg is klaar (of overgeslagen): vanaf nu geldt het gewone spel. */
  finishGuide(completed) {
    if (!this.guided) return;
    this.guided = false;
    this.save = { ...this.save, tutorialDone: true };
    this.persist();
    if (this.shift) this.shift.calm = false;
    this.policeImmunityTimer = completed ? 8 : 0;
    if (completed) this.ui.toast(t('toast.firstDelivery'), 'good');
  }

  startFreeRide() {
    trackPage('/vrij-rijden', 'Vrij rijden');
    this.guided = false;
    this.sound.init();
    this.sound.setWeather(this.weather);
    this.shift = null;
    this.isDaily = false;
    this.streetMoney = 0;
    this.weatherMode = 'dynamic';
    this.map.setClosures([]);
    this.respawnAtBakery();
    this.mode = 'freeride';
    this.paused = false;
    this.resizeCanvas();
    this.ui.onModeChange('freeride');
  }

  /** Stopt de shift (of vrij rijden) en toont de afrekening. */
  endShift() {
    if (this.mode === 'freeride') return this.quitToMenu();
    if (!this.shift) return;
    clearSavedShift(this.storage);
    this.guided = false;
    this.map.stopRivalRace?.(true);
    this.map.setClosures([]);
    this.weatherMode = 'dynamic';
    const summary = this.shift.end();
    const st = this.save.stats;
    const record = summary.earnings > st.bestEarnings && summary.deliveries > 0;
    st.shifts += 1;
    st.bestEarnings = Math.max(st.bestEarnings, summary.earnings);
    st.bestDeliveries = Math.max(st.bestDeliveries, summary.deliveries);
    st.bestShiftStars = Math.max(st.bestShiftStars, summary.shiftStars);
    st.distanceM += this.shiftDistance ?? 0;

    // Dagelijkse uitdaging: beste resultaat van vandaag bijhouden
    let daily = null;
    if (this.isDaily) {
      const today = todayKey();
      const prev = this.save.daily.date === today ? this.save.daily : { date: today, best: 0, played: 0 };
      daily = { score: summary.earnings, best: Math.max(prev.best, summary.earnings), isBest: summary.earnings > prev.best || prev.played === 0 };
      this.save.daily = { date: today, best: daily.best, played: prev.played + 1 };
    }
    this.checkAchievements(true);
    this.persist();
    this.releaseControls();
    this.sound.stopPoliceSiren();
    this.resetPolice();
    this.mode = 'menu';
    this.resizeCanvas();
    this.ui.onModeChange('menu');
    this.ui.showReport({
      ...summary,
      record,
      daily,
      streetMoney: this.streetMoney,
      rankBefore: this.rankAtStart,
      rankAfter: rankFor(this.save.stars).rank,
    });
    this.shift = null;
    this.targets = [];
    this.activeTarget = null;
  }

  quitToMenu() {
    if (this.mode === 'shift') return this.endShift();
    this.releaseControls();
    this.sound.stopPoliceSiren();
    this.resetPolice();
    this.map.setClosures([]);
    this.weatherMode = 'dynamic';
    this.mode = 'menu';
    this.paused = false;
    this.shift = null;
    this.targets = [];
    this.activeTarget = null;
    this.resizeCanvas();
    this.ui.onModeChange('menu');
  }

  pause() {
    if (this.mode === 'menu' || this.paused) return;
    this.paused = true;
    this.releaseControls();
    this.sound.stopPoliceSiren();
    this.sound.updateEngine?.(0, false);
    this.ui.onPause(true);
  }

  resume() {
    if (!this.paused) return;
    this.paused = false;
    this.lastTime = performance.now();
    this.ui.onPause(false);
  }

  respawnAtBakery() {
    const s = this.scooter;
    s.x = 1220;
    s.y = 1140;
    s.angle = Math.PI;
    s.speed = 0;
    s.lateralVelocity = 0;
    this.camera.x = s.x;
    this.camera.y = s.y;
  }

  /** Trillen op de telefoon, als de speler dat aan heeft staan. */
  vibrate(pattern) {
    if (this.save.settings.vibrate) navigator.vibrate?.(pattern);
  }

  releaseControls() {
    this.releaseOneThumb?.();
    const s = this.scooter;
    s.keys = { up: false, down: false, left: false, right: false, handbrake: false };
    s.analogSteer = 0;
    s.analogThrottle = 0;
    s.targetDirectAngle = null;
  }

  // ---------------------------------------------------------------------------
  // Orders
  // ---------------------------------------------------------------------------

  acceptOffer(id) {
    if (!this.shift) return 'bestaat-niet';
    const refusal = this.shift.accept(id);
    if (refusal) {
      this.sound.playBump();
      const msg = {
        vol: t('toast.full', { n: this.capacity }),
        'bruidstaart-alleen': t('toast.bridalAlone'),
        'bestaat-niet': t('toast.expired'),
      }[refusal];
      this.ui.toast(msg, 'warn');
    } else {
      this.sound.playUiClick?.();
    }
    return refusal;
  }

  declineOffer(id) {
    this.shift?.decline(id);
  }

  /** Bepaalt de doelen in de wereld en welk doel nu het belangrijkst is. */
  updateTargets() {
    const targets = [];
    if (this.shift) {
      const waiting = this.shift.accepted;
      if (waiting.length) {
        targets.push({
          id: 'hq',
          kind: 'bakery',
          ...BAKERY,
          icon: '🥖',
          label: t('target.load', { n: waiting.length }),
          fraction: 1,
          secondsLeft: 0,
          late: false,
          dwell: this.dwell.id === 'hq' ? this.dwell.t / LOAD_SECONDS : 0,
        });
      }
      for (const o of this.shift.loaded) {
        const left = timeLeft(o, this.shift.t);
        targets.push({
          id: o.dest.id,
          kind: 'customer',
          x: o.dest.x,
          y: o.dest.y,
          r: o.dest.r,
          icon: o.product.icon,
          label: o.customer,
          fraction: left / o.deadlineSec,
          secondsLeft: left,
          late: left < 0,
          dwell: this.dwell.id === o.dest.id ? this.dwell.t / DELIVER_SECONDS : 0,
          order: o,
        });
      }
    }
    this.targets = targets;
    // Eerst inladen, anders de klant die het eerst aan de beurt is.
    this.activeTarget =
      targets.find((t) => t.kind === 'bakery') ?? targets.filter((t) => t.kind === 'customer').sort((a, b) => a.secondsLeft - b.secondsLeft)[0] ?? null;
  }

  /**
   * Sjors rijdt naar dezelfde klant: eerst langs zijn depot (inladen), dan over de route naar de deur.
   * Zijn tempo is zo gekozen dat een gewone, vlotte rit hem net verslaat; wie rommelt of omrijdt verliest.
   */
  startRace(order) {
    const depot = SJORS_DEPOT;
    // Sjors kent de afgesloten straten en de brug ook: hij kiest dezelfde soort omweg als jij.
    const toDest = planRoute(depot, order.dest, this.shift.routeOptions);
    // Hij wacht even bij het depot en rijdt dan over het raster. De route van het depot eindigt voor de deur.
    const route = [depot, ...toDest.slice(1)];
    this.map.van.x = depot.x + 60;
    this.map.van.y = depot.y;
    this.map.startRivalRace(order.dest.id, route, 0, 2.2);
    this.raceOrderId = order.id;
    this.ui.toast(t('toast.raceStart'), 'warn');
  }

  /** Sjors is bij de klant aangekomen voordat jij er was. */
  onRivalArrived(destId) {
    const lost = this.shift?.loaded.find((o) => o.dest.id === destId && o.race);
    if (!lost) return;
    this.shift.lose(lost.id);
    this.sound.playBump();
    this.vibrate([60, 40, 60]);
    this.ui.toast(t('toast.raceLost'), 'bad');
    this.floatingTexts.push({ x: this.scooter.x, y: this.scooter.y - 40, vy: -1, text: t('float.raceLost'), color: '#ef4444', alpha: 1 });
    if (this.loadBatch) this.loadBatch.broken = true;
    this.dwell = { id: null, t: 0 };
  }

  /** Inladen en afleveren: rustig in de zone staan of uitrollen. */
  checkArrivals(dt) {
    const s = this.scooter;
    const slow = Math.abs(s.speed) < DWELL_SPEED;
    const here = slow ? this.targets.find((t) => (s.x - t.x) ** 2 + (s.y - t.y) ** 2 < (t.r + 20) ** 2) : null;
    if (!here) {
      this.dwell = { id: null, t: 0 };
      return;
    }
    if (this.dwell.id !== here.id) this.dwell = { id: here.id, t: 0 };
    this.dwell.t += dt;

    if (here.kind === 'bakery' && this.dwell.t >= LOAD_SECONDS) {
      const loaded = this.shift.load();
      this.dwell = { id: null, t: 0 };
      this.sound.playChime();
      this.ui.toast(t('toast.loaded', { items: loaded.map((o) => o.product.icon).join(' ') }), 'good');
      // Alles uit deze lading hoort bij elkaar (voor de prestatie "Volle bak")
      this.loadBatch = Object.assign(new Set(loaded.map((o) => o.id)), { total: loaded.length, broken: false });
      const race = loaded.find((o) => o.race);
      if (race) this.startRace(race);
      this.floatingTexts.push({ x: s.x, y: s.y - 40, vy: -0.8, text: t('float.loaded'), color: '#fbbf24', alpha: 1 });
    } else if (here.kind === 'customer' && this.dwell.t >= DELIVER_SECONDS) {
      this.dwell = { id: null, t: 0 };
      this.completeDelivery(here.id);
    }
  }

  completeDelivery(destId) {
    const delivered = this.shift.deliver(destId);
    if (!delivered) return;
    if (this.guided) setTimeout(() => this.finishGuide(true), 0);
    const { order, result } = delivered;
    const rankBefore = rankFor(this.save.stars).rank;
    // Uitbetaling (de shift heeft de racebonus al meegeteld), sterren en voortgang
    const raceBonus = order.race ? RACE_BONUS : 0;
    this.save.wallet = Math.round((this.save.wallet + result.total + raceBonus) * 100) / 100;
    this.save.lifetimeEarnings = Math.round((this.save.lifetimeEarnings + result.total + raceBonus) * 100) / 100;
    this.save.stars += result.stars;
    this.save.deliveredCount += 1;
    if (!this.save.stats.districts.includes(order.dest.districtId)) this.save.stats.districts = [...this.save.stats.districts, order.dest.districtId];
    if (order.race) {
      this.save.stats.raceWins += 1;
      this.map.stopRivalRace(true);
      this.ui.toast(t('toast.raceWon', { money: euro(RACE_BONUS) }), 'good');
    }
    // Volle bak: alles uit dezelfde lading, minstens 3, helemaal op tijd
    if (this.loadBatch) {
      this.loadBatch.delete(order.id);
      if (result.late) this.loadBatch.broken = true;
      if (this.loadBatch.size === 0 && this.loadBatch.total >= 3 && !this.loadBatch.broken) this.fullLoadDone = true;
    }
    this.checkAchievements();
    this.persist();

    this.sound.playCoinSound();
    this.sound.playCashRegister();
    if (this.shift.combo >= 2) setTimeout(() => this.sound.playComboFanfare(), 300);

    const s = this.scooter;
    for (let i = 0; i < 28; i++) {
      const a = Math.random() * Math.PI * 2;
      const v = 2 + Math.random() * 4;
      this.confettiParticles.push({
        x: s.x,
        y: s.y,
        vx: Math.cos(a) * v,
        vy: Math.sin(a) * v - 2.5,
        color: ['#f59e0b', '#10b981', '#3b82f6', '#ec4899', '#8b5cf6'][i % 5],
        size: 3 + Math.random() * 4,
        alpha: 1,
      });
    }
    this.floatingTexts.push({ x: s.x, y: s.y - 40, vy: -1, text: `+${euro(result.total)}`, color: '#34d399', alpha: 1 });
    this.ui.showReceipt(order, result, this.shift.combo);

    const rankAfter = rankFor(this.save.stars);
    if (rankAfter.rank > rankBefore) {
      this.sound.playUpgradeSound();
      this.ui.toast(t('toast.promotion', { title: rankAfter.title }), 'good');
    }
  }

  /** Een schok raakt alles wat in de bak zit. */
  applyShock(event) {
    if (!this.shift?.loaded.length) return;
    // Afkoeltijd: blijft iets op je scooter staan, dan telt dat niet elk beeld als een nieuwe klap.
    // Een duidelijk zwaardere klap breekt er wel doorheen.
    const sinceLast = this.time - (this.lastShockAt ?? -10);
    if (sinceLast < 0.5 && event.severity <= (this.lastShockSeverity ?? 0) * 1.3) return;
    this.lastShockAt = this.time;
    this.lastShockSeverity = event.severity;
    const hits = this.shift.shock(event.severity, this.carryContext);
    const worst = Math.max(...hits.map((h) => h.damage));
    this.sound.playPastryDamage();
    this.addShake(3 + worst * 0.3);
    // Een flinke klap voel je: het beeld bevriest heel even en de telefoon trilt.
    if (worst >= 8 && !this.reduceMotion) this.hitStop = Math.min(0.13, 0.06 + worst * 0.003);
    this.vibrate(Math.min(120, 30 + worst * 3));
    // De lading op de bak schudt mee
    if (!this.reduceMotion) this.cargoWobble.vel += (Math.random() < 0.5 ? -1 : 1) * Math.min(5, 1.5 + worst * 0.12);
    this.shockParticles.push({ x: this.scooter.x, y: this.scooter.y - 25, text: `-${worst}% 🍰`, alpha: 1 });
    const reason = ['bump', 'collision', 'pion', 'portier', 'auto', 'kat'].includes(event.type) ? t(`shock.${event.type}`) : t('shock.other');
    this.ui.toast(t('toast.shock', { reason, pct: worst }), 'bad');
  }

  // ---------------------------------------------------------------------------
  // Lus
  // ---------------------------------------------------------------------------

  frame(now) {
    // In het menu is de stad alleen een vervaagde achtergrond: 15 beelden per seconde is genoeg
    // en spaart batterij.
    // Tot de eerste aanraking staat de achtergrond stil (één beeld), zodat het laden vlot blijft.
    const menuStill = this.mode === 'menu' && !this.hasInteracted && this.lastMenuFrame !== undefined;
    if (menuStill || (this.mode === 'menu' && now - (this.lastMenuFrame ?? 0) < 66)) {
      requestAnimationFrame((t) => this.frame(t));
      return;
    }
    this.lastMenuFrame = now;
    const elapsed = Math.min(0.25, (now - this.lastTime) / 1000);
    this.lastTime = now;
    if (this.hitStop > 0) {
      // Heel even stilstaan na een harde klap; de tijd loopt daarna gewoon door.
      this.hitStop -= elapsed;
    } else if (!this.paused && !this.policeFineModalOpen) {
      this.accumulator += elapsed;
      let steps = 0;
      while (this.accumulator >= STEP && steps < MAX_STEPS_PER_FRAME) {
        this.update(STEP);
        this.accumulator -= STEP;
        steps++;
      }
      if (steps === MAX_STEPS_PER_FRAME) this.accumulator = 0; // te traag apparaat: niet blijven inhalen
    }
    this.render();
    this.ui.update(this);
    requestAnimationFrame((t) => this.frame(t));
  }

  update(dt) {
    this.time += dt;
    const playing = this.mode === 'shift' || this.mode === 'freeride';
    if (!playing) this.releaseControls();

    // Claxon ingedrukt: katten schrikken af en toe
    if (this.sound.isHonking) {
      this.hornSpookTimer = (this.hornSpookTimer || 0) + dt;
      if (this.hornSpookTimer > 0.32) {
        this.hornSpookTimer = 0;
        this.checkSpookCat();
      }
    }

    // Na een boete (20 seconden rust) of met het boetescherm open start de stad geen nieuwe achtervolging.
    this.map.policeSuppressed = this.policeImmunityTimer > 0 || this.policeFineModalOpen;
    this.map.update(dt, this.sound, this.scooter);

    if (playing) {
      this.collectCoins();
      const before = this.scooter.totalDistanceMeters || 0;
      this.scooter.prevX = this.scooter.x;
      this.scooter.prevY = this.scooter.y;
      const result = this.scooter.update(dt, this.map, this.sound, this.weather);
      this.shiftDistance = (this.shiftDistance || 0) + ((this.scooter.totalDistanceMeters || 0) - before);

      this.checkRedLightViolations(dt);
      if (this.map.checkTrafficJamSlalom?.(this.scooter)) this.onJamSlalom();
      if (result?.shockEvent) {
        if (this.mode === 'shift' && this.shift?.loaded.length) this.applyShock(result.shockEvent);
        else this.addShake(4);
        if (result.shockEvent.type === 'kat' && result.shockEvent.cat) this.triggerCatAccident(result.shockEvent.cat);
      }
      this.updatePolice(dt);
    } else {
      // In het menu draait de stad door en kijkt de camera rustig rond bij de bakkerij.
      this.sound.updateEngine?.(0, false);
    }

    this.updateWeatherParticles(dt);

    if (this.mode === 'shift' && this.shift) {
      this.shiftSaveTimer = (this.shiftSaveTimer || 0) + dt;
      if (this.shiftSaveTimer > 5) {
        this.shiftSaveTimer = 0;
        this.saveShiftProgress();
      }
      const fresh = this.shift.update(dt);
      for (const o of fresh) this.ui.onOffer(o);
      if (fresh.length) this.sound.playChime();
      this.handleEventNotices();
      this.watchBridge();
      if (this.shift.weather !== this.weather) this.setWeather(this.shift.weather, false);
      this.shift.tickCargo(dt, this.carryContext);
      this.updateTargets();
      this.checkArrivals(dt);
      if (this.shift.isOver) this.endShift();
    }

    if (playing && Math.floor(this.time * 2) !== Math.floor((this.time - dt) * 2)) this.watchDistrict();

    this.updateParticles();
    this.updateCargoWobble(dt);
    this.updateCamera(dt);
    if (this.shake > 0) this.shake = Math.max(0, this.shake - dt * 30);
  }

  /** Schermschudden, begrensd, en uit als de speler minder beweging wil. */
  /** Minder beweging: aan als je het kiest, of (op automatisch) als je telefoon erom vraagt. */
  get reduceMotion() {
    const m = this.save.settings.motion;
    return m === 'minder' || (m === 'auto' && !!this.motionQuery?.matches);
  }

  applyMotion() {
    document.documentElement.classList.toggle('reduce-motion', this.reduceMotion);
    if (this.reduceMotion) {
      this.shake = 0;
      this.hitStop = 0;
      this.cargoWobble = { angle: 0, vel: 0 };
    }
  }

  addShake(amount) {
    if (this.reduceMotion) return;
    this.shake = Math.min(10, Math.max(this.shake, amount));
  }

  /** Veer voor de lading: bochten en remmen duwen, de veer trekt terug. */
  updateCargoWobble(dt) {
    const s = this.scooter;
    const w = this.cargoWobble;
    let dAngle = s.angle - (this.prevAngle ?? s.angle);
    dAngle = Math.atan2(Math.sin(dAngle), Math.cos(dAngle)); // sprongen van 2π (joystick) negeren
    const turn = dAngle / dt; // rad/s
    this.prevAngle = s.angle;
    const accel = (s.speed - (this.prevSpeed ?? s.speed)) / dt;
    this.prevSpeed = s.speed;
    // Middelpuntvliedende kracht in bochten en een knik bij hard remmen of optrekken
    // (veer 60: een stevige bocht van 3 rad/s op topsnelheid geeft zo'n 0,25 rad kanteling)
    const force = -turn * Math.abs(s.speed) * 1.6 + Math.max(-1, Math.min(1, accel * 0.05)) * 8;
    w.vel += (force - 60 * w.angle - 6 * w.vel) * dt;
    w.angle += w.vel * dt;
    w.angle = Math.max(-0.6, Math.min(0.6, w.angle));
  }

  /** Staat de ophaalbrug open of gaat hij open? (Dan is de Hoofdstraat geen oversteek over de gracht.) */
  bridgeOpen() {
    const br = this.map.drawBridges?.[0];
    return !!br && (br.openProgress > 0.05 || br.state === 'warning' || br.state === 'opening' || br.state === 'open');
  }

  /** Geeft de speler tijd erbij als een straat dichtgaat of de brug opent terwijl hij onderweg is. */
  /** Geeft de omweg extra tijd op je deadlines. Geeft het aantal seconden terug (voor in een melding). */
  extendDeadlinesForDetour() {
    if (!this.shift) return 0;
    const from = { x: this.scooter.x, y: this.scooter.y };
    let total = 0;
    for (const o of this.shift.loaded) total = Math.max(total, extendForDetour(o, from, this.shift.routeOptions));
    return total;
  }

  /** " (+12 s erbij)" als de omweg tijd kost, anders niets. */
  detourNote(total) {
    return total > 0 ? t('toast.extraSeconds', { n: total }) : '';
  }

  /** Meldingen en gevolgen van regenbui, mist en marktdag. */
  handleEventNotices() {
    for (const n of this.shift.takeNotices()) {
      const text = EVENT_TEXT[n.event.kind];
      if (n.type === 'waarschuwing') {
        this.ui.toast(`${text.icon} ${text.warn(n.event)}`, 'warn');
        this.vibrate(30);
        this.sound.playWarning?.();
      } else if (n.type === 'start') {
        let extra = '';
        if (n.event.kind === 'markt') {
          this.map.setClosures(this.shift.closures);
          extra = this.detourNote(this.extendDeadlinesForDetour());
          this.sound.playBridgeBell?.();
        }
        this.ui.toast(`${text.icon} ${text.start(n.event)}${extra}`, n.event.kind === 'markt' ? 'warn' : 'info');
      } else {
        this.ui.toast(`${text.icon} ${text.end}`, 'info');
        if (n.event.kind === 'markt') this.map.setClosures(this.shift.closures);
      }
    }
  }

  /** De brug gaat omhoog voor een schip: een korte melding, en de omweg erbij op je deadline. */
  watchBridge() {
    const open = this.bridgeOpen();
    if (open && !this.bridgeWasOpen) {
      this.ui.toast(t('toast.bridgeUp', { note: this.detourNote(this.extendDeadlinesForDetour()) }), 'warn');
    } else if (!open && this.bridgeWasOpen) {
      this.ui.toast(t('toast.bridgeDown'), 'info');
    }
    this.bridgeWasOpen = open;
  }

  /** Meldt het als je een andere wijk binnenrijdt (alleen na twee metingen achter elkaar: geen geflikker op de grens). */
  watchDistrict() {
    const now = districtAt(this.map.destinations, this.scooter.x, this.scooter.y);
    if (!now || now === this.districtNow) {
      this.districtCandidate = null;
      return;
    }
    if (this.districtCandidate !== now) {
      this.districtCandidate = now;
      return;
    }
    const first = this.districtNow === null;
    this.districtNow = now;
    this.districtCandidate = null;
    if (!first) this.ui.toast(t('toast.district', { name: DISTRICT_NAMES[now] }), 'info');
  }

  collectCoins() {
    for (const coin of this.map.collectibleCoins ?? []) {
      if (coin.collected) continue;
      if ((this.scooter.x - coin.x) ** 2 + (this.scooter.y - coin.y) ** 2 >= 28 * 28) continue;
      coin.collected = true;
      coin.respawnTimer = 45;
      // Een munt op straat is 20 cent; de dikke munten van 5 en 10 zijn €1,00 en €2,00.
      const value = Math.round((coin.value || 1) * STREET_COIN_EURO * 100) / 100;
      this.earn(value);
      this.streetMoney += value;
      this.persist();
      this.sound.playCoinSound();
      this.floatingTexts.push({ x: coin.x, y: coin.y - 10, vy: -0.9, text: `+${euro(value)}`, color: '#fbbf24', alpha: 1 });
      for (let p = 0; p < 8; p++) {
        const a = Math.random() * Math.PI * 2;
        const v = 1 + Math.random() * 2.5;
        this.confettiParticles.push({ x: coin.x, y: coin.y, vx: Math.cos(a) * v, vy: Math.sin(a) * v - 1.2, color: '#f59e0b', size: 2.5 + Math.random() * 2.5, alpha: 0.95 });
      }
    }
  }

  /** Behendig door een file slalommen: 12 seconden tijdwinst op alles wat in de bak zit. */
  onJamSlalom() {
    this.sound.playUpgradeSound?.();
    if (this.shift) for (const o of this.shift.loaded) o.loadedAt = Math.min(this.shift.t, (o.loadedAt ?? 0) + 12);
    this.rewardMoney(0.6, t('float.slalom'));
  }

  updateParticles() {
    for (let i = this.shockParticles.length - 1; i >= 0; i--) {
      const p = this.shockParticles[i];
      p.y -= 0.6;
      p.alpha -= 0.02;
      if (p.alpha <= 0) this.shockParticles.splice(i, 1);
    }
    for (let i = this.confettiParticles.length - 1; i >= 0; i--) {
      const p = this.confettiParticles[i];
      p.x += p.vx;
      p.y += p.vy;
      p.vy += 0.15;
      p.alpha -= 0.015;
      if (p.alpha <= 0) this.confettiParticles.splice(i, 1);
    }
    for (let i = this.floatingTexts.length - 1; i >= 0; i--) {
      const f = this.floatingTexts[i];
      f.y += f.vy || -0.8;
      f.alpha -= 0.018;
      if (f.alpha <= 0) this.floatingTexts.splice(i, 1);
    }
  }

  updateCamera(dt) {
    let tx;
    let ty;
    if (this.mode === 'menu') {
      // Langzaam rondje rond de bakkerij als achtergrond van het menu
      tx = BAKERY.x + Math.cos(this.time * 0.05) * 260;
      ty = BAKERY.y + Math.sin(this.time * 0.05) * 160;
    } else {
      // Vooruit kijken in de rijrichting: hoe harder je rijdt, hoe verder je vooruit ziet.
      const s = this.scooter;
      tx = s.x + Math.cos(s.angle) * s.speed * 34;
      ty = s.y + Math.sin(s.angle) * s.speed * 34;
    }
    const k = 1 - Math.pow(1 - 0.08, dt * 60);
    this.camera.x += (tx - this.camera.x) * k;
    this.camera.y += (ty - this.camera.y) * k;

    // Dichtbij als je stilstaat, wat verder weg als je rijdt (zie camera.ts). In het menu blijft het ruim.
    const s = this.scooter;
    const driving = Math.abs(s.speed) > 0.15 || s.keys.up || s.keys.down || Math.abs(s.analogThrottle) > 0.1;
    this.stillTime = driving || this.mode === 'menu' ? 0 : (this.stillTime ?? 0) + dt;
    const base = this.baseZoom ?? this.camera.zoom;
    const target = this.mode === 'menu' ? base * 0.9 : zoomTarget(base, s.speed, this.stillTime);
    this.camera.zoom = stepZoom(this.camera.zoom, target, dt);
  }

  resizeCanvas() {
    const rect = this.canvas.getBoundingClientRect();
    const w = Math.max(1, Math.round(rect.width || window.innerWidth));
    const h = Math.max(1, Math.round(rect.height || window.innerHeight));
    // Scherp op retina, maar begrensd: meer dan ~2,4 miljoen pixels kost op telefoons te veel.
    let dpr = Math.min(window.devicePixelRatio || 1, 2);
    while (dpr > 1 && w * h * dpr * dpr > 2_400_000) dpr -= 0.25;
    // Achter het (vervaagde) menu volstaat een lage resolutie.
    if (this.mode === 'menu') dpr = 0.75;
    this.dpr = dpr;
    this.viewW = w;
    this.viewH = h;
    this.canvas.width = Math.round(w * dpr);
    this.canvas.height = Math.round(h * dpr);
    // Rijdend ongeveer 480 wereldpixels over de korte kant: rechtop en liggend even overzichtelijk (zie camera.ts).
    this.baseZoom = baseZoomFor(w, h);
    this.camera.zoom = this.baseZoom;
    this.ui?.onResize?.(this);
    // Een nieuwe canvasgrootte wist het beeld; teken direct opnieuw (ook als het menu stilstaat).
    this.render();
  }
}

applyMixins(BezorgerGame, InputMixin, WeatherMixin, PoliceMixin, RenderWorldMixin, RenderActorsMixin, RenderTargetsMixin, RenderCargoMixin, RenderMarketMixin);

export { SHIFT_SECONDS };
